import { ACCESS_TOKEN, API_BASE_SERVER } from '../../assets/constants'
import {
    parseOrderPreview,
    type ConfirmOrderProposalRequest,
    type OrderPreview,
} from '../../components/aiAssistant/domains/tradingDomain'
import { waitForInitialLoadSlot } from '../../util/initialLoadGate'

const USE_SSL = import.meta.env.VITE_USE_SSL === 'true'
const API_SCHEME = USE_SSL ? 'https://' : 'http://'
const API_BASE_URL = `${API_SCHEME}${API_BASE_SERVER}`

export type AiChatAcceptedEvent = {
    type: 'accepted'
    conversation_id: string
    status: string
    metadata?: { turn_id?: string }
}

export type AiChatEventMessage = {
    type: 'chat_event'
    conversation_id: string
    task_id: string
    status: string
    reply: string
    metadata?: Record<string, unknown>
}

export type AiChatWsMessage = AiChatAcceptedEvent | AiChatEventMessage | Record<string, unknown>

export type AiConversationSummary = {
    id: string
    title: string
    status: string
    updatedAt?: string
}

/**
 * Generic artifact embedded by the worker in a turn's message stream. The chat
 * backend stays domain-agnostic; the client matches on `type`.
 */
export type AiConversationArtifact = {
    kind: 'custom'
    type: string
    /** Client-managed lifecycle (e.g. `pending` → `applied` for filter cards). */
    status?: string
    data?: unknown
}

export type AiConversationTurnResponse = {
    id: string
    taskId?: string | null
    status: string
    userQuery: string
    assistantText: string
    agentDomain?: 'chart' | 'screener' | 'orchestrator' | string | null
    /** Generic per-turn artifacts (e.g. `type: "filter"`). */
    artifacts?: AiConversationArtifact[]
    created?: string
    updated?: string
}

export type AiConversationDetail = {
    id: string
    title: string
    status: string
    turns: AiConversationTurnResponse[]
    /** Server-rebuilt cumulative filters; present when the conversation has completed turns. */
    activeConversationFilters?: unknown
}

export type ActiveConversationFiltersResponse = {
    activeConversationFilters: unknown
}

/**
 * Chat API failure carrying the HTTP status so callers can classify it without
 * parsing the message. The message is for logs only — never render it.
 */
export class AiChatApiError extends Error {
    readonly status: number

    constructor(operation: string, status: number) {
        super(`${operation} failed (${status})`)
        this.name = 'AiChatApiError'
        this.status = status
    }
}

function authHeaders(): HeadersInit {
    const token = localStorage.getItem(ACCESS_TOKEN)
    return {
        Authorization: `Bearer ${token ?? ''}`,
        'Content-Type': 'application/json',
    }
}

export async function confirmOrderProposal(request: ConfirmOrderProposalRequest): Promise<OrderPreview> {
    const response = await fetch(`${API_BASE_URL}/chat/order-proposals/confirm`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify(request),
    })
    if (!response.ok) {
        throw new AiChatApiError('Confirm order proposal', response.status)
    }
    const body = (await response.json()) as Record<string, unknown>
    const execution = parseOrderPreview({
        ...body,
        confirmationToken: request.confirmationToken,
    })
    if (!execution) {
        throw new AiChatApiError('Confirm order proposal', 502)
    }
    return execution
}

export async function listConversations(): Promise<AiConversationSummary[]> {
    // Fired on mount by AiAssistantProvider. This uses raw fetch (not the axios
    // client), so it must opt into the initial-load gate explicitly; otherwise it
    // competes with /user/me and the first candle request during page load.
    await waitForInitialLoadSlot('/chat/conversations')
    const response = await fetch(`${API_BASE_URL}/chat/conversations`, {
        method: 'GET',
        headers: authHeaders(),
    })
    if (!response.ok) {
        throw new AiChatApiError('List conversations', response.status)
    }
    return response.json()
}

export async function createConversation(title?: string): Promise<AiConversationSummary> {
    const response = await fetch(`${API_BASE_URL}/chat/conversations`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify(title ? { title } : {}),
    })
    if (!response.ok) {
        throw new AiChatApiError('Create conversation', response.status)
    }
    return response.json()
}

export async function getConversation(conversationId: string): Promise<AiConversationDetail | null> {
    const response = await fetch(`${API_BASE_URL}/chat/conversations/${conversationId}`, {
        method: 'GET',
        headers: authHeaders(),
    })
    if (response.status === 404 || response.status === 400) {
        return null
    }
    if (!response.ok) {
        throw new AiChatApiError('Load conversation', response.status)
    }
    return response.json()
}

export async function updateTurnArtifactStatus(
    conversationId: string,
    turnId: string,
    type: string,
    status: 'applied' | 'dismissed' | 'undone',
): Promise<ActiveConversationFiltersResponse> {
    const response = await fetch(
        `${API_BASE_URL}/chat/conversations/${conversationId}/turns/${turnId}/artifact-status`,
        {
            method: 'POST',
            headers: authHeaders(),
            body: JSON.stringify({ type, status }),
        },
    )
    if (!response.ok) {
        throw new AiChatApiError('Update artifact status', response.status)
    }
    return response.json()
}
